/**
 * The privacy contract for client-side Heap analytics.
 *
 * This is the load-bearing privacy control: everything that reaches Heap is
 * built **by construction** from an allow-list. We never spread a domain model
 * or API response into a Heap payload, and {@link pickAllowed} drops any key
 * that is not explicitly permitted (drop-unless-listed, NOT a denylist — a
 * denylist fails open).
 *
 * Two surfaces are governed here:
 *  1. **Identity user properties** — the only user-identification fields allowed
 *     to reach Heap (`IDENTITY_KEYS`). Email is intentionally included: it is the
 *     identification value AND the warehouse join bridge (the existing dbt model
 *     joins Heap↔Orchard on `lower(trim(email))`). Property addresses, offer
 *     prices, investor names, EMD, and contract filenames are NEVER allowed.
 *  2. **Event base props** — the point-in-time segmentation block built by
 *     `buildBaseProps` and registered on Heap once via `addEventProperties`, so
 *     it rides on every custom event and every native pageview.
 */

import type { UserInfo } from '@/common/contexts/AuthContext'
import type { UserProfile } from '@/common/services/auth'

/**
 * Current analytics schema version. Bump when the identity/base-prop shape
 * changes in a way Redshift modeling must distinguish. Never rename an existing
 * prop — that orphans warehouse columns; mint a new key instead.
 */
export const SCHEMA_VERSION = 1

/**
 * The ONLY user-identification fields allowed to reach Heap as user properties.
 *
 * `user_id` is the FusionAuth `sub` UUID (also the Heap `identify()` value).
 * `email` is the single intentional PII field — it is the AC-3 warehouse join
 * key and is permitted ONLY as an identity user property, NEVER as an
 * event prop. The remaining keys are non-PII enums/scalars used for
 * segmentation.
 */
export const IDENTITY_KEYS = [
  'user_id',
  'email',
  'name',
  'role',
  'agent_type',
  'primary_market',
  'market_count',
  'onboarding_state',
] as const

export type IdentityKey = (typeof IDENTITY_KEYS)[number]

/** Actor role surfaced to analytics. Mirrors the FusionAuth role set we track. */
export type ActorRole = 'agent' | 'investor' | 'admin'

/** Agent type — internal = Orchard staff agent, external = third-party (3P) agent. */
export type AgentType = 'internal' | 'external' | null

/**
 * Derived onboarding lifecycle state. Coarse by design — never carries dates or
 * free text. Derived solely from whether `onboarding_completed_at` is set.
 */
export type OnboardingState = 'pre_onboarding' | 'onboarding_complete'

/**
 * The identity payload sent to Heap on `addUserProperties`. Built field-by-field
 * from the real `UserInfo` + `UserProfile` types (no object spread) so nothing
 * un-allow-listed can leak in. Values may be null where the source is null; a
 * null `agent_type` for a non-agent is EMITTED as null (present, not absent) so
 * segmentation is consistent in the warehouse.
 */
export interface IdentityProps {
  user_id: string
  email: string
  name: string | null
  role: ActorRole | null
  agent_type: AgentType
  primary_market: string | null
  market_count: number
  onboarding_state: OnboardingState
}

/**
 * Point-in-time segmentation block stamped on EVERY custom event. Heap
 * `user_properties` are current-value only, so each event must carry its own
 * snapshot for correct historical segmentation in Redshift.
 *
 * Note the deliberate naming asymmetry vs. {@link IdentityProps}: the event
 * block uses `actor_role` (the per-event actor), the identity block uses `role`.
 * They derive from the same value but are distinct surfaces.
 */
export interface BaseProps {
  app_environment: string
  actor_role: ActorRole | null
  agent_type: AgentType
  primary_market: string | null
  market_count: number
  onboarding_state: OnboardingState
  schema_version: number
}

/**
 * The recognized FusionAuth roles, in precedence order. A user may carry more
 * than one; we pick the highest-privilege match for the single `role`/`actor_role`
 * scalar. Unknown roles are ignored (never surfaced).
 */
const ROLES_BY_PRECEDENCE: ActorRole[] = ['admin', 'agent', 'investor']

/** Resolve the single actor role from a FusionAuth role list. */
export function deriveRole(roles: readonly string[]): ActorRole | null {
  for (const role of ROLES_BY_PRECEDENCE) {
    if (roles.includes(role)) {
      return role
    }
  }
  return null
}

/** Derive the coarse onboarding state from the profile's completion timestamp. */
export function deriveOnboardingState(profile: UserProfile): OnboardingState {
  return profile.onboarding_completed_at ? 'onboarding_complete' : 'pre_onboarding'
}

/**
 * UUID-shape guard for the identify value (the FusionAuth `sub`). Mirrors the
 * org convention (avm-webapp / website-qwik both UUID_REGEX-gate their Heap
 * identity). We accept any RFC-4122 variant; we only assert the *shape*, not the
 * version, since FusionAuth subs are v4 but we do not want to be brittle.
 */
const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

export function isUuid(value: unknown): value is string {
  return typeof value === 'string' && UUID_REGEX.test(value)
}

/**
 * Email-shape guard. Intentionally permissive (presence of a single `@` with
 * non-empty local/domain parts) — Heap stores the value verbatim; we only guard
 * against obviously-malformed identity, not validate deliverability.
 */
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

export function isEmailShaped(value: unknown): value is string {
  return typeof value === 'string' && EMAIL_REGEX.test(value)
}

/**
 * Build the Heap identity user-properties object field-by-field from the real
 * `UserInfo` + `UserProfile` types.
 *
 * Typed by the read shape (`UserProfile`, NOT `UserProfileUpdateInput`) so
 * passing the PUT request DTO is a compile error. `markets` is `string[] | null`
 * on `UserProfile`; we derive `primary_market` (first entry) and `market_count`.
 *
 * The returned object contains ONLY `IDENTITY_KEYS`. A null `agent_type` for a
 * non-agent is included as null (present, not absent).
 */
export function buildIdentity(user: UserInfo, profile: UserProfile): IdentityProps {
  const markets = profile.markets ?? []
  return {
    user_id: user.user_id,
    email: user.email,
    name: deriveName(user),
    role: deriveRole(user.roles),
    agent_type: profile.agent_type,
    primary_market: markets.length > 0 ? markets[0] : null,
    market_count: markets.length,
    onboarding_state: deriveOnboardingState(profile),
  }
}

/**
 * Derive a display name from auth claims. Prefers the full name, falls back to
 * "given family", then null. Never falls back to email (email is its own
 * dedicated identity key and must not be duplicated into `name`).
 */
function deriveName(user: UserInfo): string | null {
  if (user.full_name) {
    return user.full_name
  }
  const parts = [user.given_name, user.family_name].filter(Boolean)
  return parts.length > 0 ? parts.join(' ') : null
}

/**
 * Build the shared base-prop block from the current identity snapshot.
 * Registered on Heap once via `addEventProperties` (see `setEventBaseProps` in
 * `heap.ts`) so it rides on every custom event and every native pageview.
 */
export function buildBaseProps(identity: IdentityProps, appEnvironment: string): BaseProps {
  return {
    app_environment: appEnvironment,
    actor_role: identity.role,
    agent_type: identity.agent_type,
    primary_market: identity.primary_market,
    market_count: identity.market_count,
    onboarding_state: identity.onboarding_state,
    schema_version: SCHEMA_VERSION,
  }
}

/**
 * Return a copy of `props` containing ONLY the allow-listed keys. Any key not in
 * `allowedKeys` is dropped. This is the runtime half of the two-way (compile-time
 * + runtime) allow-list enforcement: even if a forbidden prop slips past the
 * type system (e.g. via `any`), it never reaches Heap.
 *
 * Drop-unless-listed: a key absent from `allowedKeys` is removed even if present
 * in `props`. `undefined` values are dropped; explicit `null` values are kept
 * (a null enum is meaningful segmentation, not noise).
 */
export function pickAllowed<T extends Record<string, unknown>>(
  props: T,
  allowedKeys: readonly string[]
): Record<string, unknown> {
  const allowed = new Set(allowedKeys)
  const result: Record<string, unknown> = {}
  for (const [key, value] of Object.entries(props)) {
    if (allowed.has(key) && value !== undefined) {
      result[key] = value
    }
  }
  return result
}
